import { DateRange } from "react-day-picker";

import { addDays, format, isAfter, isFuture, isPast, subDays } from "date-fns";

import { DatePickerMode } from "./DatePicker.types";

export const getTimeDropdownOptions = (
  stepMinutes: number = 15,
  displayInUTC: boolean = false
) => {
  const tzOffset = -new Date().getTimezoneOffset() / 60;

  return Array.from({ length: 24 * (60 / stepMinutes) }, (_, i) => {
    const h = Math.floor((i * stepMinutes) / 60);
    const m = (i * stepMinutes) % 60;
    const local = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;

    let label = local;
    if (displayInUTC) {
      const utcHour = (h - tzOffset + 24) % 24;
      const utc = `${utcHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      label += ` (${utc} UTC)`;
    }

    return { value: local, label };
  });
};

export const setSelectedDateTime = (
  currentDate: Date | undefined,
  newDate?: Date,
  newTime?: string,
  currentFromValue: string = '12:00'
): { isoString: string; date: Date; fromValue: string } => {
  const refDate = newDate || currentDate || new Date();
  const [h, m] = (newTime ?? currentFromValue).split(':').map(Number);
  const localDate = new Date(
    refDate.getFullYear(),
    refDate.getMonth(),
    refDate.getDate(),
    h,
    m,
    0,
    0
  );
  return {
    isoString: localDate.toISOString(),
    date: localDate,
    fromValue: newTime ?? currentFromValue
  };
};

export const getDateRange = ({
  value,
  selectedDay,
  fixedRangeDays,
  shouldExcludeFuture,
  shouldExcludePast,
  disableDatesAfter
}: {
  value?: DateRange;
  selectedDay: Date;
  fixedRangeDays?: number;
  shouldExcludeFuture?: boolean;
  shouldExcludePast?: boolean;
  disableDatesAfter?: Date;
}) => {
  if (fixedRangeDays === undefined) {
    return value;
  }
  const endDate = addDays(selectedDay, fixedRangeDays);

  if (disableDatesAfter && isAfter(endDate, disableDatesAfter)) {
    const adjustedTo = disableDatesAfter;
    const adjustedFrom = subDays(adjustedTo, fixedRangeDays);
    return { from: adjustedFrom, to: adjustedTo };
  }

  if (shouldExcludeFuture && isFuture(endDate)) {
    const adjustedTo = new Date();
    const adjustedFrom = subDays(adjustedTo, fixedRangeDays);
    return { from: adjustedFrom, to: adjustedTo };
  }

  if (shouldExcludePast && isPast(selectedDay)) {
    const adjustedTo = new Date();
    const adjustedFrom = subDays(adjustedTo, fixedRangeDays);
    return { from: adjustedFrom, to: adjustedTo };
  }

  return { from: selectedDay, to: endDate };
};

export const getDisplayDate = (
  mode: DatePickerMode,
  date?: Date,
  dateRange?: DateRange,
  displayInUTC: boolean = false
): string | undefined => {
  if (mode === DatePickerMode.SINGLE) {
    if (!date) return undefined;
    if (displayInUTC) {
      return format(date, "PPP HH:mm 'UTC'");
    }
    return format(date, 'PPP HH:mm');
  }

  if (!dateRange?.from) {
    return undefined;
  }

  if (dateRange.to) {
    return `${format(dateRange.from, 'PPP')} - ${format(dateRange.to, 'PPP')}`;
  }

  return format(dateRange.from, 'PPP');
};
